import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { config, COUNTRY } from '@/config';
import * as Styles from './useCreditCardPayment.styles';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { XRAY_ENABLED_FINGERPRINT, XRAY_INSTALLMENTS_MODAL, XRAY_VERIFY_CARD_CHECK } from '@/config/GrowthBook/constants';
import { CALLER, PaymentStatusTypes } from '../usePaymentModal/usePaymentModal.types';
import { loadInvoices, updateStatusInvoiceToConfirmingPayment } from '@/redux/actions/invoices';
import { getPaymentMethod } from '@/redux/actions/paymentMethod';
import { ERROR_CC_METHOD, RECEIVE_GATORPAY_PAAS_SAVE_CREDITCARD } from '@/redux/actions/actionsTypes';
import { enqueueSnackbar } from '@/redux/actions/notifications';
import { notifierError } from '@/utils/Application/notifier';
import { PHISHING_STATUS_ANALYSIS } from '@/config/invoices/invoiceStatus';
import { isBRServer } from '@/utils/Validators/validation';
import { getCreditCardAttempt, saveCreditCard, saveCreditCardAttempt } from '@/redux/actions/creditCardPaas';
import { billingActions } from '@/redux/modules';
import { generateRecaptchaToken, loadReCaptchaPaaS } from '@/utils/Application/reCaptcha';
import { Trans } from 'react-i18next';
import { loadCreditCard } from '@/redux/actions/creditCard';

const useCreditCardPayment = ({
  creditCardInstallments,
  setCcButtonEnabled,
  setCcButtonLoading,
  selectedInstallment,
  setPaymentStatus,
  useCardOnNextRenovation = false,
  handleSwitchPaymentMethod,
  invoiceId,
  caller,
  showIframeMiniCreditCard = true,
  setError,
  callFastCheckout,
}) => {
  const [heightIframe, setHeightIframe] = useState(260);
  const [canSubmit, setCanSubmit] = useState(false);

  const summaryState = useSelector(state => state.summary);
  const creditCardAttempt = useSelector(state => state.creditCardAttempt);

  const enabledFingerprint = useFeatureIsOn(XRAY_ENABLED_FINGERPRINT);
  const isCreditCardInstallmentsEnabled = useFeatureIsOn(XRAY_INSTALLMENTS_MODAL);
  const isVerifyCardCheckActive = useFeatureIsOn(XRAY_VERIFY_CARD_CHECK);
  const isFastCheckoutCaller = caller === CALLER.FASTCHECKOUT;
  const isSubscriptionsCaller = caller === CALLER.SUBSCRIPTIONS;
  const isInvoicesCaller = caller === CALLER.INVOICES;

  const iframeRef = useRef(null);
  const dispatch = useDispatch();

  const {
    IFRAME_URL,
    CLIENT_ID,
    IFRAME_PAGENAME,
  } = config.PAAS_CONFIG;

  const providerIdentifier = enabledFingerprint && `${COUNTRY}${summaryState.id}${Date.now()}`;

  const onSetNewAlertError = useCallback((error) => {
    setError && setError(error);
  }, [setError]);

  const handleError = useCallback((error) => {
    if (isFastCheckoutCaller) {
      return onSetNewAlertError({ error: true, title: '', message: <Trans i18nKey={error} /> });
    }

    dispatch(enqueueSnackbar(error, notifierError));
  }, [onSetNewAlertError, dispatch, isFastCheckoutCaller]);

  const onSaveCreditCard = useCallback(() => {
    const hasSelectedInstallment = creditCardInstallments && creditCardInstallments.filter(installment => installment.gateway === selectedInstallment).length > 0;

    const enabledSubmit = !!selectedInstallment && !isCreditCardInstallmentsEnabled ? true : hasSelectedInstallment;
    setCanSubmit(enabledSubmit);
  }, [selectedInstallment, creditCardInstallments, isCreditCardInstallmentsEnabled]);

  const changePaymentMethod = useCallback(() => {
    setCcButtonLoading(true);
    dispatch(getPaymentMethod(selectedInstallment.value, invoiceId, providerIdentifier, useCardOnNextRenovation)).then((action) => {
      const isError = action.type === ERROR_CC_METHOD;

      setCcButtonLoading(false);

      if (isError) {
        switch (action.status) {
          case 'INVALID_INVOICE_STATUS':
            setPaymentStatus && setPaymentStatus(PaymentStatusTypes.FAILURE_CREDIT_CARD);
            updateStatusInvoiceToConfirmingPayment(invoiceId);
            break;
          case 'PENDING_ANALYSIS':
            loadInvoices('Unpaid', true);
            setPaymentStatus && setPaymentStatus(PaymentStatusTypes.SUCCESS_GENERIC);
            break;
          default:
            setPaymentStatus && setPaymentStatus(PaymentStatusTypes.FAILURE_CREDIT_CARD);
            if (Object.prototype.hasOwnProperty.call(action, 'key') && action.key === PHISHING_STATUS_ANALYSIS) {
              loadInvoices('Unpaid', true);
            }
            break;
        }
      }

      if (!isError) {
        setPaymentStatus && setPaymentStatus(PaymentStatusTypes.SUCCESS_GENERIC);
        loadInvoices('Unpaid', true);
        updateStatusInvoiceToConfirmingPayment(invoiceId);
      }
    });
  }, [
    dispatch,
    invoiceId,
    providerIdentifier,
    setPaymentStatus,
    useCardOnNextRenovation,
    selectedInstallment,
    setCcButtonLoading,
  ]);

  const sendCurrentCreditCard = useCallback(() => {
    if (isSubscriptionsCaller) {
      handleSwitchPaymentMethod();
    }

    if (isInvoicesCaller || isFastCheckoutCaller) {
      changePaymentMethod();
    }
  }, [changePaymentMethod, handleSwitchPaymentMethod, isFastCheckoutCaller, isSubscriptionsCaller, isInvoicesCaller]);

  const postMessage = useCallback((body) => {
    const { contentWindow } = document.getElementById('token-iframe');
    contentWindow.postMessage(JSON.stringify({
      securePayMessage: true,
      body,
    }), IFRAME_URL);
  }, [IFRAME_URL]);

  const callGetToken = useCallback((token) => {
    postMessage({
      callFunction: {
        name: 'hgLatamGetToken',
        arguments: [token],
      },
    });
  }, [postMessage]);

  const submitCreditCard = () => {
    if (isBRServer) {
      dispatch(getCreditCardAttempt());
      if (creditCardAttempt !== undefined && !creditCardAttempt.response) {
        handleError('snackbar.creditCard.paymentFailByAttemptsLimit');
        setCcButtonEnabled(true);
        setCcButtonLoading(false);
        return;
      }
    }

    setCcButtonEnabled(false);
    setCcButtonLoading(true);
    postMessage({
      callFunction: {
        name: 'hgLatamValidateAll',
        arguments: [],
      },
    });
  };

  const checkSubscriptionsToChangePaymentMethod = useCallback(() => {
    dispatch(billingActions.subscriptions.requestSubscriptionsNotInCreditCard());
  }, [dispatch]);

  const onMessage = useCallback((event) => {
    if (event.data.securePayMessage !== true) {
      return false;
    }
    let { body } = event.data;
    if (body.constructor === String) {
      try {
        body = JSON.parse(body);
      // eslint-disable-next-line no-console
      } catch (e) { console.log(e); }
    }
    if (body.constructor === Object) {
      if (body.isValid !== undefined && body.fields !== undefined) {
        if (!body.isValid) {
          handleError('snackbar.creditCard.paas.errorSaveCreditCard');
          setCcButtonEnabled(true);
          setCcButtonLoading(false);
          return;
        }
        generateRecaptchaToken(config.NEW_RECAPTCHA_PAAS, 'userexistis').then((token) => {
          callGetToken(token);
        }).catch(() => {
          handleError('snackbar.creditCard.paas.errorSaveCreditCard');
          setCcButtonEnabled(true);
          setCcButtonLoading(false);
        });
      }
      if (body.creditCard !== undefined && body.creditCard.data !== '') {
        body.creditCard.token = body.id;
        if (isVerifyCardCheckActive) {
          if (body.properties !== undefined && body.properties.Status !== undefined && body.properties.Status !== 1) {
            handleError('snackbar.creditCard.paas.errorVerifyCard');
            setPaymentStatus && setPaymentStatus(PaymentStatusTypes.FAILURE_CREDIT_CARD);
            setCcButtonEnabled(true);
            setCcButtonLoading(false);

            if (isBRServer) {
              dispatch(saveCreditCardAttempt(body.creditCard.token));
            }
            return;
          }
        }
        setCcButtonLoading(true);
        dispatch(saveCreditCard(body.creditCard)).then((data) => {
          onSaveCreditCard();
          checkSubscriptionsToChangePaymentMethod();

          if (isSubscriptionsCaller) {
            handleSwitchPaymentMethod();
          }

          if (isInvoicesCaller) {
            changePaymentMethod();
          }

          if (isFastCheckoutCaller) {
            callFastCheckout();
            dispatch(loadCreditCard());
            setCcButtonLoading(false);
            setCcButtonEnabled(true);
          }

          if (isBRServer && data.type === RECEIVE_GATORPAY_PAAS_SAVE_CREDITCARD) {
            dispatch(saveCreditCardAttempt(body.creditCard.token, 'ip'));
          }
        });
      }
      if (body.size !== undefined && body.size.height !== 0) {
        setHeightIframe(body.size.height);
      }
    }
  }, [
    isVerifyCardCheckActive,
    dispatch,
    checkSubscriptionsToChangePaymentMethod,
    setCcButtonEnabled,
    setPaymentStatus,
    setHeightIframe,
    callGetToken,
    onSaveCreditCard,
    changePaymentMethod,
    setCcButtonLoading,
    handleSwitchPaymentMethod,
    handleError,
    isFastCheckoutCaller,
    isSubscriptionsCaller,
    isInvoicesCaller,
    callFastCheckout,
  ]);

  useEffect(() => {
    loadReCaptchaPaaS(true);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  useEffect(() => {
    dispatch(getCreditCardAttempt());
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [IFRAME_URL, onMessage]);

  const renderIframe = (
    <Styles.Iframe
      ref={iframeRef}
      height={`${heightIframe}px`}
      width="100%"
      src={`${IFRAME_URL}/payment/${IFRAME_PAGENAME}?${showIframeMiniCreditCard ? 'creditshow=show' : ''}&${isFastCheckoutCaller ? 'fastcheckout' : 'portal'}=show&clientId=${typeof CLIENT_ID === 'string'
        ? CLIENT_ID
        : CLIENT_ID[summaryState.personType.toLowerCase()]
      }&country=${COUNTRY}`}
      id="token-iframe"
      title="cc"
      isFastCheckoutCaller={isFastCheckoutCaller}
    />
  );

  return {
    canSubmit,
    submitCreditCard,
    sendCurrentCreditCard,
    renderIframe,
  };
};

export default useCreditCardPayment;
